<ng-container>
    <div class="container-fluid">
        <div class="card-body row py-3 px-0 medications-hldr">
            <!-- Entry Form -->
            <div class="w-100 medication-form">
                <h4 class="mb-3" *ngIf="editMode">Edit Medication</h4>
                <h4 class="mb-3" *ngIf="!editMode">Add Medication</h4>

                <!-- Mode Toggle -->
                <div class="mb-3 mode-toggle">
                    <p-radioButton name="entryMode" value="search" [(ngModel)]="entryMode" label="Database Lookup" inputId="modeSearch"></p-radioButton>
                    <p-radioButton name="entryMode" value="manual" [(ngModel)]="entryMode" label="Manual Entry" inputId="modeManual" class="ml-3"></p-radioButton>
                </div>

                <!-- Search Mode -->
                <div *ngIf="entryMode === 'search'" class="mb-3">
                    <label>Medication Name <span class="text-danger">*</span></label>
                    <div class="search-container">
                        <input
                            type="text"
                            class="form-control form-control-lg form-control-solid"
                            placeholder="Search medications (e.g. Lisinopril, Metformin)..."
                            [(ngModel)]="searchTerm"
                            (input)="onSearchInput()"
                            (blur)="hideResults()"
                            [ngClass]="{ 'is-invalid': submitted && !selectedMedication }"
                        />
                        <div *ngIf="searching" class="search-spinner">
                            <i class="pi pi-spin pi-spinner"></i>
                        </div>
                        <div *ngIf="showResults && searchResults.length" class="search-results">
                            <div
                                *ngFor="let result of searchResults"
                                class="search-result-item"
                                (mousedown)="selectMedication(result)"
                            >
                                <span class="result-name">{{ result.name }}</span>
                                <span class="result-count" *ngIf="result.strengths?.length">{{ result.strengths.length }} form(s)</span>
                            </div>
                        </div>
                    </div>
                    <small class="search-hint">Search 10,000+ medications — data from NIH RxTerms, updated monthly</small>
                    <div *ngIf="submitted && !selectedMedication" class="text-danger mt-1" style="font-size: 0.85rem;">
                        Please select a medication from the search results
                    </div>

                    <!-- Selected Medication Confirmation -->
                    <div *ngIf="selectedMedication" class="selected-medication mt-2">
                        <span class="badge-medication">
                            <i class="pi pi-check-circle mr-1"></i>
                            {{ selectedMedication.name }}
                            <span *ngIf="selectedStrength"> - {{ selectedStrength }}</span>
                        </span>
                    </div>

                    <!-- Strength / Form -->
                    <div *ngIf="selectedMedication" class="mt-3">
                        <label>Strength / Form</label>
                        <input
                            type="text"
                            class="form-control form-control-lg form-control-solid"
                            placeholder="e.g. 10 MG Oral Tab"
                            [(ngModel)]="selectedStrength"
                        />
                        <div *ngIf="selectedMedication?.strengths?.length" class="strength-chips mt-2">
                            <button
                                *ngFor="let s of selectedMedication.strengths"
                                type="button"
                                class="strength-chip"
                                [class.active]="selectedStrength === s"
                                (click)="selectStrength(s)"
                            >
                                {{ s }}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Manual Mode -->
                <div *ngIf="entryMode === 'manual'" class="mb-3">
                    <div class="row">
                        <div class="col-6">
                            <label>Medication Name <span class="text-danger">*</span></label>
                            <input
                                type="text"
                                class="form-control form-control-lg form-control-solid"
                                placeholder="Enter medication name"
                                [(ngModel)]="manualMedicationName"
                                [ngClass]="{ 'is-invalid': submitted && !manualMedicationName.trim() }"
                            />
                            <div *ngIf="submitted && !manualMedicationName.trim()" class="text-danger mt-1" style="font-size: 0.85rem;">
                                Medication name is required
                            </div>
                        </div>
                        <div class="col-6">
                            <label>Strength / Form</label>
                            <input
                                type="text"
                                class="form-control form-control-lg form-control-solid"
                                placeholder="e.g. 10 MG Oral Tab"
                                [(ngModel)]="manualStrength"
                            />
                        </div>
                    </div>
                </div>

                <!-- Shared Fields: Dosage, Route, Frequency -->
                <div class="row mb-3">
                    <div class="col-4">
                        <label>Dosage</label>
                        <input
                            type="text"
                            class="form-control form-control-lg form-control-solid"
                            placeholder="e.g. 1 tablet, 5 mL"
                            [(ngModel)]="dosage"
                        />
                    </div>
                    <div class="col-4">
                        <label>Route</label>
                        <p-dropdown
                            [options]="routeOptions"
                            [(ngModel)]="selectedRoute"
                            placeholder="Select route"
                            optionLabel="label"
                            optionValue="value"
                            [showClear]="true"
                            styleClass="w-100 input-group input-group-lg input-group-solid"
                        >
                            <ng-template let-opt pTemplate="selectedItem">
                                {{ opt?.label }}
                            </ng-template>
                        </p-dropdown>
                    </div>
                    <div class="col-4">
                        <label>Frequency</label>
                        <p-dropdown
                            [options]="frequencyOptions"
                            [(ngModel)]="selectedFrequency"
                            placeholder="Select frequency"
                            optionLabel="label"
                            optionValue="value"
                            [showClear]="true"
                            styleClass="w-100 input-group input-group-lg input-group-solid"
                        >
                            <ng-template let-opt pTemplate="selectedItem">
                                {{ opt?.label }}
                            </ng-template>
                        </p-dropdown>
                    </div>
                </div>

                <!-- Notes -->
                <div class="mb-3">
                    <label>Notes</label>
                    <textarea
                        class="form-control form-control-lg form-control-solid"
                        placeholder="Special instructions, allergies, etc."
                        [(ngModel)]="medicationNotes"
                        rows="2"
                    ></textarea>
                </div>

                <!-- Action Buttons -->
                <div class="flex flex-row justify-content-end align-items-center gap-2">
                    <button *ngIf="editMode" pButton pRipple label="Cancel" icon="pi pi-times" class="p-button-text text-secondary" (click)="cancelEdit()"></button>
                    <button
                        pButton pRipple
                        [label]="editMode ? 'Update' : 'Add Medication'"
                        [icon]="editMode ? 'pi pi-check' : 'pi pi-plus'"
                        class="p-button-primary"
                        [disabled]="loading"
                        (click)="onSubmit()"
                    >
                        <span *ngIf="loading" class="spinner-border spinner-border-sm mr-1"></span>
                    </button>
                </div>
            </div>

            <!-- Medications List -->
            <div class="w-100 mt-4">
                <app-inline-loader *ngIf="loadingData"></app-inline-loader>
                <div *ngIf="!loadingData">
                    <div class="flex flex-column w-100" *ngIf="leadMedications && leadMedications.length">
                        <h4 class="mb-2">Current Medications</h4>
                        <div class="medication-list">
                            <div *ngFor="let med of leadMedications" class="medication-card">
                                <!-- Structured JSON medication -->
                                <ng-container *ngIf="parseMedication(med.value) as data">
                                    <div class="med-card-body">
                                        <div class="med-info">
                                            <div class="med-name">
                                                {{ data.medication }}
                                                <span class="med-source-badge" *ngIf="data.source === 'rxterms'">RxTerms</span>
                                            </div>
                                            <div class="med-details" *ngIf="data.strength || data.dosage || data.route || data.frequency">
                                                <span *ngIf="data.strength" class="med-detail-tag">{{ data.strength }}</span>
                                                <span *ngIf="data.dosage" class="med-detail-tag">{{ data.dosage }}</span>
                                                <span *ngIf="data.route" class="med-detail-tag">{{ data.route }}</span>
                                                <span *ngIf="data.frequency" class="med-detail-tag">{{ data.frequency }}</span>
                                            </div>
                                            <div class="med-notes" *ngIf="data.notes">
                                                <i class="pi pi-info-circle mr-1"></i> {{ data.notes }}
                                            </div>
                                        </div>
                                        <div class="med-actions">
                                            <i class="pi pi-pencil blue-icon mx-2" (click)="editMedication(med)"></i>
                                            <i class="pi pi-trash red-icon" (click)="deleteUserMedication(med.id)"></i>
                                        </div>
                                    </div>
                                </ng-container>

                                <!-- Legacy HTML medication -->
                                <ng-container *ngIf="!parseMedication(med.value)">
                                    <div class="med-card-body">
                                        <div class="med-info">
                                            <div class="med-legacy" [innerHTML]="med.value"></div>
                                        </div>
                                        <div class="med-actions">
                                            <i class="pi pi-pencil blue-icon mx-2" (click)="editMedication(med)"></i>
                                            <i class="pi pi-trash red-icon" (click)="deleteUserMedication(med.id)"></i>
                                        </div>
                                    </div>
                                </ng-container>
                            </div>
                        </div>
                    </div>
                    <div *ngIf="!leadMedications || !leadMedications.length" class="text-muted py-3">
                        No medications recorded.
                    </div>
                </div>
            </div>
        </div>
    </div>
</ng-container>

<p-confirmDialog styleClass="dark-glass-dialog" appendTo="body" [style]="{ width: '50vw' }" key="leadMedicationDelete" [baseZIndex]="10000"></p-confirmDialog>
